import React, { useState, useEffect, useRef } from 'react';
import useImageCrop from '../hooks/useImageCrop';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, PenLine, Image as ImageIcon, MapPin,
  ShieldCheck, Info, Trash2, Plus, Clock, Check, GitBranch, Ban
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import ReactQuill from 'react-quill';
import AIReviewButton from '../components/AIReviewButton';
import { generateReportHash, generateTimestampSeal, generateAccessToken } from '../utils/crypto';
import TemplatePicker from '../components/common/TemplatePicker';

/* Cabeçalho de etapa — mesma numeração 01/02/03 da landing */
const StepHeading = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-display text-2xl font-bold text-navy" aria-hidden="true">{number}</span>
    <h2 className="font-semibold text-base text-ink uppercase tracking-wider">{title}</h2>
  </div>
);

export default function EditReport() {
  const { id } = useParams();
  const quillRef = useRef(null);
  const navigate = useNavigate();
  const { templates, signatures, locations, fetchReports } = useData();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Estado ORIGINAL do laudo como carregado do banco — nunca sobrescrito pelas
  // edições do usuário. É a partir dele que decidimos se editar deve mutar a
  // linha (rascunho) ou criar uma nova versão (laudo já assinado).
  const [originalReport, setOriginalReport] = useState(null);

  const [formData, setFormData] = useState({
    patient_name: '',
    patient_age: '',
    patient_sex: '',
    patient_species: '',
    patient_breed: '',
    patient_owner: '',
    patient_is_neutered: false,
    exam_date: '',
    requesting_professional: '',
    request_reason: '',
    template_id: '',
    signature_id: '',
    location_id: '',
    content: '',
    folder_id: null,
    status: 'draft'
  });

  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const { data: report, error } = await supabase
          .from('reports')
          .select('*, report_images(image_url)')
          .eq('id', id)
          .single();
        
        if (error) throw error;
        if (report) {
          // Converter data amigável de volta para ISO para o input datetime-local
          // O formato salvo é "DD/MM/YYYY, HH:mm" (toLocaleString pt-BR; laudos antigos sem vírgula)
          let isoDate = '';
          const match = String(report.exam_date || '').match(/(\d{2})\/(\d{2})\/(\d{4})[,\s]+(\d{2}):(\d{2})/);
          if (match) {
            const [, day, month, year, hour, minute] = match;
            isoDate = `${year}-${month}-${day}T${hour}:${minute}`;
          }

          setOriginalReport(report);
          setFormData({
            ...report,
            exam_date: isoDate || new Date().toISOString().slice(0, 16),
            patient_is_neutered: report.patient_is_neutered || false
          });
          setUploadedImages(report.report_images?.map(img => img.image_url) || []);
        }
      } catch (err) {
        console.error(err);
        navigate('/app/meus-laudos');
      } finally {
        setIsLoading(false);
      }
    };
    fetchReport();
  }, [id, navigate]);

  const handleTemplateChange = (e) => {
    const tid = e.target.value;
    const template = templates.find(t => t.id === tid);
    setFormData(prev => ({
      ...prev,
      template_id: tid,
      content: template ? template.content : prev.content
    }));
  };

  const { cropImages, cropModal } = useImageCrop();

  const handleImageUpload = async (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = '';
    if (!picked.length) return;
    const files = await cropImages(picked, { allowSkip: true });
    if (!files) return;

    setIsSaving(true);
    try {
      const newImages = [];
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('report-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('report-images')
          .getPublicUrl(filePath);

        newImages.push(publicUrl);
      }
      setUploadedImages([...uploadedImages, ...newImages]);
    } catch (err) {
      alert('Erro no upload: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Um laudo já ASSINADO nunca é mutado — editar sempre publica uma NOVA
  // versão (parent_report_id + version), e a versão anterior fica marcada
  // como superseded_at, permanecendo acessível pelo link/token originais
  // (recomendação: nunca comprometer o hash já divulgado a terceiros).
  const isSignedOriginal = originalReport?.status === 'signed';
  const isRevokedOriginal = originalReport?.status === 'revoked';

  const handleCreateNewVersion = async () => {
    if (!formData.patient_name || !formData.content) {
      alert('Por favor, preencha o nome do paciente e o conteúdo do laudo.');
      return;
    }
    if (!formData.signature_id) {
      alert('Selecione uma assinatura para emitir a nova versão.');
      return;
    }

    setIsSaving(true);
    try {
      const displayDate = new Date(formData.exam_date).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
      const nowIso = new Date().toISOString();
      const newVersion = (originalReport.version || 1) + 1;
      const hash = await generateReportHash({
        content: formData.content,
        patientName: formData.patient_name,
        examDate: displayDate,
        signatureId: formData.signature_id,
        locationId: formData.location_id,
        version: newVersion,
      });

      const { data: newReport, error: insertError } = await supabase
        .from('reports')
        .insert([{
          user_id: user.id,
          patient_name: formData.patient_name,
          patient_owner: formData.patient_owner,
          patient_age: formData.patient_age,
          patient_sex: formData.patient_sex,
          patient_species: formData.patient_species,
          patient_breed: formData.patient_breed,
          patient_is_neutered: formData.patient_is_neutered,
          exam_date: displayDate,
          requesting_professional: formData.requesting_professional,
          request_reason: formData.request_reason,
          template_id: formData.template_id,
          signature_id: formData.signature_id,
          location_id: formData.location_id,
          content: formData.content,
          folder_id: formData.folder_id || null,
          status: 'signed',
          signed_at: nowIso,
          parent_report_id: id,
          version: newVersion,
          hash,
          access_token: generateAccessToken(6),
          timestamp_seal: await generateTimestampSeal(hash, nowIso),
          timestamp_verified_at: nowIso,
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Imagens pertencem à versão nova — as da versão anterior permanecem
      // intocadas (o laudo antigo tem que continuar exatamente como foi lido).
      if (uploadedImages.length > 0) {
        await supabase.from('report_images').insert(
          uploadedImages.map(url => ({ report_id: newReport.id, image_url: url }))
        );
      }

      // Marca a versão anterior como substituída — sem tocar em content/hash/access_token dela.
      await supabase.from('reports').update({ superseded_at: nowIso, updated_at: nowIso }).eq('id', id);

      // Auditoria (best-effort — não deve bloquear o fluxo se a tabela/policy divergir).
      await supabase.from('audit_logs').insert([
        { report_id: id, user_id: user.id, event_type: 'version_up', metadata: { new_report_id: newReport.id, new_version: newVersion } },
        { report_id: newReport.id, user_id: user.id, event_type: 'sign', metadata: { supersedes_report_id: id, version: newVersion } },
      ]).then(() => {}, () => {});

      await fetchReports();
      navigate('/app/meus-laudos');
    } catch (err) {
      alert('Erro ao emitir nova versão: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (statusOverride = null) => {
    // Laudo já assinado: editar sempre vira nova versão, nunca update in-place.
    if (isSignedOriginal) return handleCreateNewVersion();

    if (!formData.patient_name || !formData.content) {
      alert('Por favor, preencha o nome do paciente e o conteúdo do laudo.');
      return;
    }

    setIsSaving(true);
    try {
      const status = statusOverride || formData.status;

      // Formatar data para exibição (DD/MM/YYYY HH:mm)
      const displayDate = new Date(formData.exam_date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Primeira emissão a partir de um rascunho: gera hash/token novos.
      let sealed = {};
      if (status === 'signed') {
        const nowIso = new Date().toISOString();
        const hash = await generateReportHash({
          content: formData.content,
          patientName: formData.patient_name,
          examDate: displayDate,
          signatureId: formData.signature_id,
          locationId: formData.location_id,
          version: formData.version || 1,
        });
        sealed = {
          hash,
          access_token: formData.access_token || generateAccessToken(6),
          timestamp_seal: formData.timestamp_seal || await generateTimestampSeal(hash, nowIso),
          timestamp_verified_at: formData.timestamp_verified_at || nowIso,
        };
      }

      const { error: reportError } = await supabase
        .from('reports')
        .update({
          patient_name: formData.patient_name,
          patient_owner: formData.patient_owner,
          patient_age: formData.patient_age,
          patient_sex: formData.patient_sex,
          patient_species: formData.patient_species,
          patient_breed: formData.patient_breed,
          patient_is_neutered: formData.patient_is_neutered,
          exam_date: displayDate,
          requesting_professional: formData.requesting_professional,
          request_reason: formData.request_reason,
          template_id: formData.template_id,
          signature_id: formData.signature_id,
          location_id: formData.location_id,
          content: formData.content,
          status,
          signed_at: status === 'signed' ? new Date().toISOString() : formData.signed_at,
          updated_at: new Date().toISOString(),
          ...sealed,
        })
        .eq('id', id);

      if (reportError) throw reportError;

      // Atualizar imagens (seguro: laudo ainda era rascunho, nunca foi divulgado com hash estável)
      await supabase.from('report_images').delete().eq('report_id', id);
      if (uploadedImages.length > 0) {
        const imagesPayload = uploadedImages.map(url => ({
          report_id: id,
          image_url: url
        }));
        await supabase.from('report_images').insert(imagesPayload);
      }

      await fetchReports();
      navigate('/app/meus-laudos');
    } catch (err) {
      alert('Erro ao salvar: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'clean'],
    ],
  };

  const isVet = user?.type === 'veterinario';
  const labelType = isVet ? 'CRMV' : 'CRM';

  if (isLoading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin" role="status" aria-label="Carregando laudo" />
    </div>
  );

  // Laudo revogado é terminal — não faz sentido "editar" algo que não vale
  // mais. Quem precisar de um documento válido deve emitir um laudo novo.
  if (isRevokedOriginal) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <div className="w-16 h-16 bg-danger-tint text-danger rounded-card flex items-center justify-center mx-auto mb-6">
        <Ban className="w-8 h-8" aria-hidden="true" />
      </div>
      <h1 className="font-display text-2xl font-bold text-navy mb-2">Este laudo foi revogado</h1>
      <p className="text-sm text-muted mb-2 max-w-md mx-auto">
        Laudos revogados não podem ser editados. A revogação é definitiva.
        {originalReport?.revocation_reason && <> Motivo registrado: <span className="text-ink font-medium">{originalReport.revocation_reason}</span>.</>}
      </p>
      <div className="flex items-center justify-center gap-3 mt-8">
        <button onClick={() => navigate('/app/meus-laudos')} className="btn-secondary">Voltar para meus laudos</button>
        <button onClick={() => navigate('/app/novo-laudo')} className="btn-convert">Emitir um novo laudo</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-32">
      {cropModal}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2.5 min-h-[44px] min-w-[44px] hover:bg-paper-alt rounded-card transition-colors"
          aria-label="Voltar"
        >
          <ArrowLeft className="w-6 h-6 text-muted" aria-hidden="true" />
        </button>
        <h1 className="font-display text-2xl font-bold text-navy">
          {isSignedOriginal ? `Revisar laudo (v${originalReport?.version || 1} → v${(originalReport?.version || 1) + 1})` : 'Editar laudo'}
        </h1>
      </div>

      {isSignedOriginal && (
        <div className="bg-paper-alt border border-line rounded-card px-5 py-4 mb-8 text-sm text-muted">
          <p>
            Este é o laudo <strong className="text-ink">v{originalReport?.version || 1}</strong>, já emitido e registrado, e não é alterado.
            Ao salvar, uma <strong className="text-ink">nova versão (v{(originalReport?.version || 1) + 1})</strong> é publicada com hash e senha próprios.
            A versão atual continua acessível pelo link já compartilhado, marcada como substituída.
          </p>
        </div>
      )}

      <div className="space-y-8">
        {/* Etapa 01: identificação */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <StepHeading number="01" title="Identificação do paciente" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="edit-patient-name" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Nome do paciente *</label>
              <input
                id="edit-patient-name"
                type="text"
                className="input-field"
                value={formData.patient_name}
                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="edit-patient-owner" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">{isVet ? 'Tutor / Proprietário' : 'Responsável'}</label>
              <input
                id="edit-patient-owner"
                type="text"
                className="input-field"
                value={formData.patient_owner}
                onChange={(e) => setFormData({ ...formData, patient_owner: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="edit-patient-age" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Idade</label>
                <input
                  id="edit-patient-age"
                  type="text"
                  className="input-field"
                  value={formData.patient_age}
                  onChange={(e) => setFormData({ ...formData, patient_age: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="edit-patient-sex" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Sexo</label>
                <select
                  id="edit-patient-sex"
                  className="input-field appearance-none"
                  value={formData.patient_sex}
                  onChange={(e) => setFormData({ ...formData, patient_sex: e.target.value })}
                >
                  <option value="">Selecione</option>
                  <option value="M">Macho</option>
                  <option value="F">Fêmea</option>
                </select>
              </div>
            </div>

            {isVet && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Espécie / Raça</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Espécie"
                      aria-label="Espécie"
                      value={formData.patient_species}
                      onChange={(e) => setFormData({ ...formData, patient_species: e.target.value })}
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Raça"
                      aria-label="Raça"
                      value={formData.patient_breed}
                      onChange={(e) => setFormData({ ...formData, patient_breed: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, patient_is_neutered: !formData.patient_is_neutered })}
                    className={`flex items-center justify-between px-4 py-2.5 min-h-[44px] rounded-card border transition-colors ${
                      formData.patient_is_neutered
                        ? 'bg-paper-alt border-navy text-navy'
                        : 'bg-paper border-line text-muted hover:border-navy/40'
                    }`}
                  >
                    <span className="text-xs font-semibold uppercase">Castrado?</span>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.patient_is_neutered ? 'border-navy bg-navy' : 'border-line'
                    }`}>
                      {formData.patient_is_neutered && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
                    </div>
                  </button>
                </div>
              </>
            )}

            <div>
              <label htmlFor="edit-exam-date" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Data e hora do exame</label>
              <div className="relative">
                  <input
                  id="edit-exam-date"
                  type="datetime-local"
                  className="input-field"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Etapa 02: conteúdo e fotos */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <StepHeading number="02" title="Conteúdo do laudo" />
            <div className="flex items-center gap-3">
              <label htmlFor="edit-template-select" className="text-xs font-semibold text-muted uppercase tracking-wide">Modelo:</label>
              <TemplatePicker id="edit-template-select" templates={templates} value={formData.template_id} onChange={handleTemplateChange} placeholder="Manter conteúdo atual" />
            </div>
          </div>

          <AIReviewButton quillRef={quillRef} reportId={id} />
          <div className="rich-editor-wrapper">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.content}
              onChange={(val) => setFormData({ ...formData, content: val })}
              modules={quillModules}
              placeholder="Descreva os achados do exame aqui..."
            />
          </div>

          <div className="mt-8">
            <h3 className="mb-4 text-navy font-semibold text-sm uppercase tracking-wider">Documentação fotográfica</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {uploadedImages.map((url, i) => (
                <div key={i} className="relative group aspect-square rounded-card overflow-hidden bg-paper-alt border border-line">
                  <img src={url} alt={`Anexo ${i+1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => setUploadedImages(uploadedImages.filter((_, idx) => idx !== i))}
                    className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-full opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                    aria-label={`Remover anexo ${i+1}`}
                  >
                    <Trash2 className="w-3 h-3" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-card border-2 border-dashed border-line hover:border-navy hover:bg-paper-alt flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors text-muted hover:text-navy">
                <Plus className="w-6 h-6" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase">Adicionar</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            </div>
          </div>
        </section>

        {/* Etapa 03: assinatura e local */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <StepHeading number="03" title="Assinatura e local" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <span className="block text-xs font-semibold text-muted uppercase tracking-wide mb-3">Sua assinatura</span>
              <div className="grid grid-cols-1 gap-3">
                {signatures.map(sig => (
                  <button
                    key={sig.id}
                    onClick={() => setFormData({ ...formData, signature_id: sig.id })}
                    className={`flex items-center gap-4 p-4 rounded-card border transition-colors text-left ${
                      formData.signature_id === sig.id
                        ? 'border-navy bg-paper-alt'
                        : 'border-line hover:border-navy/40'
                    }`}
                  >
                    {sig.image_url ? (
                      <img src={sig.image_url} alt={sig.name} className="w-12 h-12 object-contain bg-white rounded border border-line" />
                    ) : (
                      <div className="w-12 h-12 bg-paper-alt rounded flex items-center justify-center text-muted">
                        <PenLine className="w-6 h-6" aria-hidden="true" />
                      </div>
                    )}
                    <div>
                      <span className="block font-semibold text-ink">{sig.name}</span>
                      <span className="text-xs text-muted font-mono">{labelType} {sig.crm}/{sig.uf}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="block text-xs font-semibold text-muted uppercase tracking-wide mb-3">Local de atendimento</span>
              <div className="grid grid-cols-1 gap-3">
                {locations.map(loc => (
                  <button
                    key={loc.id}
                    onClick={() => setFormData({ ...formData, location_id: loc.id })}
                    className={`flex items-center gap-4 p-4 rounded-card border transition-colors text-left ${
                      formData.location_id === loc.id
                        ? 'border-navy bg-paper-alt'
                        : 'border-line hover:border-navy/40'
                    }`}
                  >
                    {loc.logo_url ? (
                      <img src={loc.logo_url} alt={loc.name} className="w-12 h-12 object-contain bg-white rounded border border-line" />
                    ) : (
                      <div className="w-12 h-12 bg-paper-alt rounded flex items-center justify-center text-muted">
                        <MapPin className="w-6 h-6" aria-hidden="true" />
                      </div>
                    )}
                    <div>
                      <span className="block font-semibold text-ink">{loc.name}</span>
                      <span className="text-xs text-muted line-clamp-1">{loc.address}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Barra de emissão: rascunho é neutro; o ÚNICO botão selo da tela é a emissão (ação irreversível, gera hash) */}
      <div className="fixed bottom-0 left-0 right-0 bg-paper/90 backdrop-blur-md border-t border-line p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-end gap-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!isSignedOriginal && (
              <button
                onClick={() => handleSubmit('draft')}
                disabled={isSaving}
                className="btn-secondary flex-1 sm:flex-none"
              >
                {isSaving ? 'Salvando...' : 'Salvar rascunho'}
              </button>
            )}
            <button
              onClick={() => handleSubmit('signed')}
              disabled={isSaving || !formData.signature_id}
              className="btn-convert flex-1 sm:flex-none px-8"
            >
              {isSaving ? 'Publicando...' : isSignedOriginal ? `Publicar versão ${(originalReport?.version || 1) + 1}` : 'Emitir e registrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}